import React from 'react';

// Animated jar: fills from empty → full with sparkle notes rising
export default function JarSVG({ fill = 0, size = 160, animate = false }) {
  const maxH = 92;
  const fillH = Math.round(fill * maxH);
  const y = 172 - fillH;

  // Note colors for animated rising notes
  const noteColors = [
    { g1: '#FFD166', g2: '#F4A500' },
    { g1: '#FF8FAB', g2: '#D63384' },
    { g1: '#2DD4BF', g2: '#0F9E8A' },
    { g1: '#FFB347', g2: '#E07B00' },
    { g1: '#C8F', g2: '#A040CC' },
  ];

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 160 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ filter: 'drop-shadow(0 0 18px rgba(255,209,102,0.5)) drop-shadow(0 0 6px rgba(255,140,60,0.3))' }}
    >
      <defs>
        {/* Liquid: warm gold-to-amber */}
        <linearGradient id="jG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFD166" stopOpacity="0.98"/>
          <stop offset="100%" stopColor="#F4A500" stopOpacity="0.85"/>
        </linearGradient>
        {/* Liquid shimmer overlay */}
        <linearGradient id="jGS" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#FFE9A0" stopOpacity="0.35"/>
          <stop offset="40%" stopColor="#FFD166" stopOpacity="0"/>
          <stop offset="100%" stopColor="#FFBF47" stopOpacity="0.22"/>
        </linearGradient>
        {/* Glass sheen */}
        <linearGradient id="jGl" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.11"/>
          <stop offset="100%" stopColor="#fff" stopOpacity="0.01"/>
        </linearGradient>
        {/* Inner glow */}
        <radialGradient id="jGc" cx="50%" cy="55%" r="52%">
          <stop offset="0%" stopColor="#FFD166" stopOpacity="0.4"/>
          <stop offset="100%" stopColor="#F4A500" stopOpacity="0"/>
        </radialGradient>
        {/* Jar body glass */}
        <linearGradient id="jBody" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#1C1420" stopOpacity="1"/>
          <stop offset="30%" stopColor="#231928" stopOpacity="1"/>
          <stop offset="100%" stopColor="#1A1220" stopOpacity="1"/>
        </linearGradient>
        {/* Glitter sparkle gradient */}
        <radialGradient id="spk" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FFE580"/>
          <stop offset="100%" stopColor="#FFD166" stopOpacity="0"/>
        </radialGradient>
        {/* Note gradients */}
        {noteColors.map((c, i) => (
          <linearGradient key={i} id={`ng${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={c.g1}/>
            <stop offset="100%" stopColor={c.g2}/>
          </linearGradient>
        ))}
        {/* Clip to jar interior */}
        <clipPath id="jC">
          <path d="M28 82 Q26 102 25 122 Q24 157 30 172 Q40 187 80 187 Q120 187 130 172 Q136 157 135 122 Q134 102 132 82 Z"/>
        </clipPath>
      </defs>

      {/* ── Lid / cap ── */}
      <rect x="42" y="53" width="76" height="15" rx="4" fill="#2B1C0E" stroke="rgba(255,209,102,0.32)" strokeWidth="1.2"/>
      <rect x="38" y="63" width="84" height="11" rx="3" fill="#1E1408" stroke="rgba(255,209,102,0.22)" strokeWidth="1"/>
      {/* Lid highlight */}
      <rect x="46" y="56" width="32" height="3.5" rx="1.5" fill="rgba(255,255,255,0.08)"/>

      {/* ── Jar body ── */}
      <path
        d="M28 82 Q26 102 25 122 Q24 157 30 172 Q40 187 80 187 Q120 187 130 172 Q136 157 135 122 Q134 102 132 82 Z"
        fill="url(#jBody)"
        stroke="rgba(255,209,102,0.26)"
        strokeWidth="1.5"
      />

      {/* Neck band */}
      <path d="M38 74 L38 82 L122 82 L122 74 Z" fill="#251A0A" stroke="rgba(255,209,102,0.2)" strokeWidth="1"/>

      {/* ── Liquid fill ── */}
      {fillH > 0 && (
        <g clipPath="url(#jC)">
          {/* Main liquid body */}
          <rect x="25" y={y} width="110" height={fillH + 20} fill="url(#jG)"/>
          {/* Shimmer overlay */}
          <rect x="25" y={y} width="110" height={fillH + 20} fill="url(#jGS)"/>
          {/* Surface meniscus */}
          <ellipse cx="80" cy={y} rx="55" ry="6.5" fill="#FFE080" opacity="0.55"/>
          {/* Inner depth glow */}
          <ellipse cx="80" cy="145" rx="42" ry="30" fill="url(#jGc)"/>
        </g>
      )}

      {/* ── Rising note animations (visible when fill > 0.1) ── */}
      {fill > 0.1 && [0,1,2,3,4].map(i => {
        const xPos = 38 + i * 18;
        const delay = `${i * 0.55}s`;
        const dur = `${2.4 + i * 0.3}s`;
        return (
          <g key={i} clipPath="url(#jC)" opacity="0.82">
            <g>
              <animateTransform
                attributeName="transform"
                type="translate"
                values={`${xPos},${y + 30}; ${xPos - 5},${y - 60}`}
                dur={dur}
                begin={delay}
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.42 0 0.58 1"
              />
              <animate
                attributeName="opacity"
                values="0; 0.9; 0.9; 0"
                dur={dur}
                begin={delay}
                repeatCount="indefinite"
              />
              <rect width="14" height="10" rx="2" fill={`url(#ng${i})`} opacity="0.85"/>
              {/* Tiny folded corner on note */}
              <polygon points="10,0 14,0 14,4" fill="rgba(0,0,0,0.22)"/>
            </g>
          </g>
        );
      })}

      {/* ── Glass highlight streak ── */}
      <path d="M35 92 Q33 132 35 162" stroke="rgba(255,255,255,0.06)" strokeWidth="5" strokeLinecap="round" fill="none"/>

      {/* ── Glass sheen overlay ── */}
      <path
        d="M28 82 Q26 102 25 122 Q24 157 30 172 Q40 187 80 187 Q120 187 130 172 Q136 157 135 122 Q134 102 132 82 Z"
        fill="url(#jGl)"
      />

      {/* ── Glitter sparkles (floating outside jar) ── */}
      {fill > 0 && (<>
        {/* Gold bubble/glitter 1 */}
        <circle cx="62" cy="108" r="2.2" fill="#FFD166" opacity="0.65">
          <animate attributeName="opacity" values="0.65;0.1;0.65" dur="2.1s" repeatCount="indefinite"/>
          <animate attributeName="r" values="2.2;1.2;2.2" dur="2.1s" repeatCount="indefinite"/>
        </circle>
        {/* Glitter 2 */}
        <circle cx="100" cy="138" r="1.8" fill="#FFE580" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0;0.5" dur="2.7s" repeatCount="indefinite"/>
        </circle>
        {/* Glitter 3 */}
        <circle cx="74" cy="160" r="2.5" fill="#FFBF47" opacity="0.45">
          <animate attributeName="opacity" values="0.45;0.9;0.45" dur="1.9s" repeatCount="indefinite"/>
        </circle>
        {/* Teal sparkle */}
        <circle cx="115" cy="115" r="1.6" fill="#2DD4BF" opacity="0.5">
          <animate attributeName="opacity" values="0.5;0.05;0.5" dur="3.1s" repeatCount="indefinite"/>
        </circle>
        {/* Rose sparkle */}
        <circle cx="48" cy="148" r="1.4" fill="#FF8FAB" opacity="0.45">
          <animate attributeName="opacity" values="0.45;0;0.45" dur="2.4s" repeatCount="indefinite" begin="0.8s"/>
        </circle>

        {/* Star sparkles outside jar */}
        <g opacity="0.7">
          <animate attributeName="opacity" values="0.7;0.1;0.7" dur="1.8s" repeatCount="indefinite"/>
          <line x1="18" y1="100" x2="18" y2="108" stroke="#FFD166" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="14" y1="104" x2="22" y2="104" stroke="#FFD166" strokeWidth="1.5" strokeLinecap="round"/>
        </g>
        <g opacity="0.6">
          <animate attributeName="opacity" values="0.6;0;0.6" dur="2.3s" repeatCount="indefinite" begin="0.5s"/>
          <line x1="142" y1="128" x2="142" y2="136" stroke="#2DD4BF" strokeWidth="1.5" strokeLinecap="round"/>
          <line x1="138" y1="132" x2="146" y2="132" stroke="#2DD4BF" strokeWidth="1.5" strokeLinecap="round"/>
        </g>
        <g opacity="0.55">
          <animate attributeName="opacity" values="0.55;0;0.55" dur="2.9s" repeatCount="indefinite" begin="1.2s"/>
          <line x1="16" y1="155" x2="16" y2="161" stroke="#FF8FAB" strokeWidth="1.3" strokeLinecap="round"/>
          <line x1="13" y1="158" x2="19" y2="158" stroke="#FF8FAB" strokeWidth="1.3" strokeLinecap="round"/>
        </g>
      </>)}
    </svg>
  );
}
